// src/pages/dataInput/types.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared types used across all DataInput wizard components.
// Single import source — no component should import directly from
// sheetValidation.ts, ddlParser.ts, or API.ts for DataInput types.
// ─────────────────────────────────────────────────────────────────────────────

import { FileClassification, SheetClassification, SheetWarning } from "./utils/sheetValidation";
import { ParsedTable, ParsedRelationship } from "./utils/ddlParser";
import { CatalogTable, DDLRelationship } from "../API";
import { SelectedSource } from "../connection/ConnectionManagerModal";

export type SourceKind = "file" | "ddl" | "db" | "catalog";

export type Tool = "BUILD_DG_USING_DATA" | "BUILD_DQ_USING_DATA" | "BUILD_MDM_USING_DATA";

export type SheetMode = "sub_entities" | "separate_entities";

/** One uploaded file the user added in Step 1. */
export interface UploadedFile {
  name: string;
  file: File;
}

/** Per-sheet config (only relevant for Excel uploads). */
export interface SheetConfig {
  fileName:         string;
  sheetName:        string;
  displayName:      string;
  description:      string;
  included:         boolean;
  rowCount?:        number;
  colCount?:        number;
  mode:             "enriched" | "typed" | "raw" | "empty";
  acceptanceReason: string;
  accepted:         boolean;
  /** Client-side data quality warnings (blank fields, duplicates, special chars) */
  warnings?:        SheetWarning[];
  /** How many data rows will actually be processed (total - blank - invalid) */
  processableRows?: number;
  /** Unique field count after dedup (processable - duplicate extras) */
  uniqueFields?:    number;
}

/** A relationship that ends up in the FK editor — applies to all source kinds. */
export interface Relationship {
  from_table:  string;
  from_column: string;
  to_table:    string;
  to_column:   string;
  source:      "manual" | "ddl" | "db" | "catalog" | "suggested";
  confidence?: number;
  reason?:     string;
}

/**
 * Normalized payload emitted by a source component when the user is done
 * picking. Step 2 reads from this to know how to populate field requirements
 * and what to send on submit.
 */
export interface SourcePayload {
  kind:          SourceKind;
  label:         string;
  sheetMode:     SheetMode;

  files?:        UploadedFile[];
  sheetConfigs?: SheetConfig[];

  parsedTables?: ParsedTable[];

  dbSource?:     SelectedSource;

  catalogTarget?:      "collibra" | "alation";
  catalogSourceLabel?: string;
  catalogTables?:      CatalogTable[];

  availableTables: { prefix: string; label: string; columns: string[] }[];
  relationships:   Relationship[];
}

/** Defaults a source can pre-fill into Step 2 for the user to override. */
export interface SourceDefaults {
  domain?:             string;
  entity?:             string;
  entity_description?: string;
  business_context?:   string;
  system_name?:        string;
}

// ── Discriminated narrowings ──────────────────────────────────────────────────

export interface FileSourcePayload extends SourcePayload {
  kind:         "file";
  files:        UploadedFile[];
  sheetConfigs: SheetConfig[];
}

export interface DDLSourcePayload extends SourcePayload {
  kind:         "ddl";
  parsedTables: ParsedTable[];
}

export interface DBSourcePayload extends SourcePayload {
  kind:     "db";
  dbSource: SelectedSource;
}

export interface CatalogSourcePayload extends SourcePayload {
  kind:               "catalog";
  catalogTarget:      "collibra" | "alation";
  catalogSourceLabel: string;
  catalogTables:      CatalogTable[];
}

export const isFileSource    = (p: SourcePayload | null): p is FileSourcePayload    => !!p && p.kind === "file";
export const isDDLSource     = (p: SourcePayload | null): p is DDLSourcePayload     => !!p && p.kind === "ddl";
export const isDBSource      = (p: SourcePayload | null): p is DBSourcePayload      => !!p && p.kind === "db";
export const isCatalogSource = (p: SourcePayload | null): p is CatalogSourcePayload => !!p && p.kind === "catalog";

/** Re-exports so consumers don't have to import from utils directly. */
export type {
  FileClassification,
  SheetClassification,
  SheetWarning,
  ParsedTable,
  ParsedRelationship,
  DDLRelationship,
  SelectedSource,
};